import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const BillingContext = createContext(null);

export const BillingProvider = ({ children }) => {
  const { user } = useAuth();
  const [tier, setTier] = useState("free");
  const [subscription, setSubscription] = useState(null);
  const [freeHoldingLimit, setFreeHoldingLimit] = useState(5);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, reason: null });

  const refresh = useCallback(async () => {
    if (!user) {
      setTier("free");
      setSubscription(null);
      return;
    }
    try {
      const { data } = await api.get("/billing/me");
      setTier(data.tier);
      setSubscription(data.subscription);
      setFreeHoldingLimit(data.free_holding_limit ?? 5);
    } catch (err) {
      // 401 expected pre-auth; suppress all errors (user can't act on them)
      void err;
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isPaid = tier === "pro" || tier === "founder_lifetime";

  const openUpgrade = useCallback((reason) => {
    setUpgradeModal({ open: true, reason: reason || null });
  }, []);
  const closeUpgrade = useCallback(() => {
    setUpgradeModal({ open: false, reason: null });
  }, []);

  // Memoize context value so consumers don't re-render when the parent
  // re-renders with an unchanged set of children.
  const value = useMemo(
    () => ({ tier, subscription, isPaid, freeHoldingLimit, refresh, upgradeModal, openUpgrade, closeUpgrade }),
    [tier, subscription, isPaid, freeHoldingLimit, refresh, upgradeModal, openUpgrade, closeUpgrade],
  );

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => useContext(BillingContext);
